import React, { useEffect, useState } from 'react';
import { fetchIntegrationStatus, sendDocumentToProcoreFolder } from '../services/api';
import { Button, Card, ErrorText, errorMessage } from './ui';
import ProcoreFolderBrowser from './ProcoreFolderBrowser';

// Sending a document back to Procore's Documents tool, into a chosen folder.
//
// Separate from the existing "Send to Procore" card, which targets the
// Drawings and Images tools. Those are different Procore resources with no
// document folders - a drawing goes to an area, a photo to an album - so this
// could not be bolted onto them, and anything that is simply a document had no
// route back at all.
//
// The folder is chosen rather than typed. Procore's own tree is the thing the
// person already knows, and a free-text path would quietly create near-
// duplicates the way the photo album field does.
export default function ProcoreSendToDocumentsCard({ doc, project, onSent }) {
  const procoreProjectId = String(project?.procoreProjectId || '');
  const [connected, setConnected] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  // Where the browser currently is. Sending files to wherever you have
  // navigated to is the whole interaction - there is no separate "choose this
  // one" step, because the folder you are looking at IS the destination.
  const [here, setHere] = useState({ id: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Nothing to check for a project that isn't from Procore - and no request
    // made for it either. Same gate the push card uses.
    if (!procoreProjectId) return undefined;
    let live = true;
    fetchIntegrationStatus()
      .then((status) => {
        const procore = (status?.providers || []).find((p) => p.id === 'procore');
        if (live) setConnected(Boolean(procore?.connected));
      })
      .catch(() => { if (live) setConnected(false); });
    return () => { live = false; };
  }, [procoreProjectId]);

  if (!procoreProjectId || !connected || !doc?.blobName) return null;

  async function send() {
    setBusy(true);
    setError('');
    setResult('');
    try {
      await sendDocumentToProcoreFolder({
        procoreProjectId,
        blobName: doc.blobName,
        fileName: doc.fileName,
        folderId: here.id,
      });
      setResult(`Sent to ${here.name || 'Procore'}.`);
      setBrowsing(false);
      // So a list showing this file can re-read it and mark it as sent. The
      // push stamps the blob, so the badge only needs the list refetched - not
      // state kept in two places and reconciled.
      onSent?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Send to Procore Documents"
      hint="Files the Documents tool, in the folder you choose. Drawings and photos have their own route above."
    >
      <ErrorText>{error}</ErrorText>
      {result ? <p className="saved">{result}</p> : null}

      {!browsing ? (
        <Button secondary compact onClick={() => { setBrowsing(true); setResult(''); }}>
          Choose a folder
        </Button>
      ) : null}

      {browsing ? (
        <>
          {/* Folders only - no renderFile. Which files are already in a
              Procore folder is not a decision this card asks anyone to make,
              and listing them would imply it was. */}
          <ProcoreFolderBrowser
            projectId={procoreProjectId}
            rootName={project?.name || 'Documents'}
            onFolderChange={({ id, name }) => setHere({ id, name })}
          />
          <p className="card-hint" style={{ marginTop: 8 }}>
            Sending to <strong>{here.name || project?.name || 'the project root'}</strong>. Open a
            folder to change it.
          </p>
          <Button compact onClick={send} disabled={busy}>
            {busy ? 'Sending…' : 'Send here'}
          </Button>
        </>
      ) : null}
    </Card>
  );
}
